import * as React from "react";
import useSWR from "swr";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { snackbarStore } from "src/store/snackbarStore";
import { useDelegateStore } from "src/store/delegate/delegate.store";
import DelegateUpsertDialog from "src/components/delegate/DelegateUpsertDialog";
import {
  getDelegateListApi,
  getDelegateApi,
  createDelegateApi,
  updateDelegateApi,
  deleteDelegateApi,
  type DelegateItem,
  type DelegateListItem,
} from "src/api/delegate/delegateApi";

const SIZE_OPTIONS = [10, 20, 25, 50, 100];

export default function DelegateManagePage() {
  const {
    delegateName,
    page, // UI 1-based
    size,
    order,
    setQuery,
    dialogOpen,
    editItem,
    openCreate,
    openEdit,
    closeDialog,
  } = useDelegateStore();

  // list swr
  const listKey = React.useMemo(
    () => ["delegate/list", delegateName, page, size, order] as const,
    [delegateName, page, size, order]
  );

  const { data, isLoading, mutate } = useSWR(listKey, async () => {
    const res = await getDelegateListApi({
      delegateName: delegateName.trim() || undefined,
      page: Math.max(0, page - 1), // UI 1-based -> API 0-based
      size,
      order,
    });
    return res;
  });

  const list = data?.data?.content ?? [];
  const totalPages = data?.data?.page?.totalPages ?? 1;
  const totalElements = data?.data?.page?.totalElements ?? 0;
  const pageNumber0 = data?.data?.page?.number ?? 0; // 0-based
  // edit detail
  const [detail, setDetail] = React.useState<DelegateItem | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);

  React.useEffect(() => {
    let alive = true;

    (async () => {
      if (!dialogOpen || !editItem?.id) {
        setDetail(null);
        return;
      }

      try {
        setDetailLoading(true);
        const res = await getDelegateApi(editItem.id);
        if (!alive) return;

        if (res.statusCode === 1) setDetail(res.data);
        else setDetail(null);
      } finally {
        if (alive) setDetailLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [dialogOpen, editItem?.id]);

  const onSubmit = async (payload: {
    delegateName: string;
    delegateDesc: string;
    file?: File | null;
  }) => {
    const hasNewFile = !!payload.file;

    try {
      setSaving(true);

      if (editItem?.id) {
        const res = await updateDelegateApi(
          editItem.id,
          {
            delegateName: payload.delegateName,
            delegateDesc: payload.delegateDesc,
          },
          payload.file
        );

        if (res.statusCode !== 1) return;

        snackbarStore.getState().addSnackbar({
          message: "수정 완료",
          severity: "success",
        });
      } else {
        const res = await createDelegateApi(
          {
            delegateName: payload.delegateName,
            delegateDesc: payload.delegateDesc,
          },
          payload.file
        );

        if (res.statusCode !== 1) return;

        snackbarStore.getState().addSnackbar({
          message: "등록 완료",
          severity: "success",
        });
      }

      closeDialog();
      setDetail(null);

      // ✅ 파일 첨부로 저장한 경우에만 재조회
      if (hasNewFile) {
        await mutate();
      }
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (item: DelegateListItem) => {
    const ok = window.confirm(`"${(item as any).delegateName ?? item.id}" 삭제할까?`);
    if (!ok) return;

    try {
      setSaving(true);
      const res = await deleteDelegateApi(item.id);
      if (res.statusCode !== 1) return;

      snackbarStore.getState().addSnackbar({
        message: "삭제 완료",
        severity: "success",
      });

      await mutate();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Stack spacing={0.5}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                입점사 목록
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                총 {totalElements.toLocaleString()}건
              </Typography>
            </Stack>
          </Stack>

          {/* 검색/옵션 */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, flexWrap: "wrap" }}>
            <TextField
              label="입점사명 검색"
              value={delegateName}
              onChange={(e) => setQuery({ delegateName: e.target.value, page: 1 })}
              size="small"
              sx={{ width: 320 }}
            />
            <Button variant="outlined" onClick={() => mutate()} disabled={isLoading}>
              검색
            </Button>

            <Box sx={{ flex: 1 }} />

            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel id="delegate-size-label">페이지 크기</InputLabel>
              <Select
                labelId="delegate-size-label"
                label="페이지 크기"
                value={size}
                onChange={(e) => setQuery({ size: Number(e.target.value) || 20, page: 1 })}
              >
                {SIZE_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}개
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={openCreate} disabled={saving}>
              등록
            </Button>
          </Stack>

          {isLoading ? (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={90}>ID</TableCell>
                    <TableCell>입점사명</TableCell>
                    <TableCell>설명</TableCell>
                    <TableCell width={180}>생성일</TableCell>
                    <TableCell width={120} align="right">
                      액션
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {list.map((it) => (
                    <TableRow key={it.id} hover>
                      <TableCell>{it.id}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>
                        {(it as any).delegateName ?? "-"}
                      </TableCell>
                      <TableCell>{(it as any).delegateDesc ?? "-"}</TableCell>
                      <TableCell>
                        {it.createdAt ? new Date(it.createdAt).toLocaleString() : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openEdit(it)} disabled={saving}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onDelete(it)} disabled={saving}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {list.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        데이터가 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>

              {/* ✅ 페이지네이션 */}
              <Stack direction="row" justifyContent="center" sx={{ pt: 2 }}>
                <Pagination
                  count={Math.max(1, totalPages)}
                  page={page}
                  onChange={(_, nextPage) => setQuery({ page: nextPage })}
                  color="primary"
                  shape="rounded"
                  disabled={isLoading || saving}
                />
              </Stack>
            </>
          )}
        </Paper>
      </Box>

      <DelegateUpsertDialog
        open={dialogOpen}
        item={detail}
        listItem={editItem}
        loading={saving || detailLoading}
        onClose={() => {
          closeDialog();
          setDetail(null);
        }}
        onSubmit={onSubmit}
      />
    </>
  );
}
